import supabase from '../supabaseClient';
import { waitForAuthSession } from '../utils/waitForAuthSession';

/** New visit if away longer than this (reopen tab, return next day, etc.) */
const VISIT_GAP_MS = 30 * 60 * 1000;
const ROUTE_THROTTLE_MS = 10 * 60 * 1000;
const VISIT_PREFIX = 'statbox_platform_access_visit:';
const ROUTE_PREFIX = 'statbox_platform_access_route:';

/**
 * Classify the client as phone or laptop for platform_access_events.
 */
export function detectDeviceCategory() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'laptop';
  }

  const ua = navigator.userAgent || '';
  const mobileUa = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const tabletLike = navigator.maxTouchPoints > 1 && window.innerWidth < 1024;
  return mobileUa || tabletLike ? 'phone' : 'laptop';
}

function visitKey(surface, userId) {
  return `${VISIT_PREFIX}${surface}:${userId}`;
}

function routeKey(surface, routePath) {
  return `${ROUTE_PREFIX}${surface}:${routePath || '/'}`;
}

function getLastVisitAt(surface, userId) {
  if (typeof localStorage === 'undefined' || !userId) return 0;
  const raw = localStorage.getItem(visitKey(surface, userId));
  const ts = Number(raw);
  return Number.isFinite(ts) ? ts : 0;
}

function markVisit(surface, userId) {
  if (typeof localStorage === 'undefined' || !userId) return;
  localStorage.setItem(visitKey(surface, userId), String(Date.now()));
}

function shouldLogVisit(surface, userId) {
  if (!userId) return false;
  return Date.now() - getLastVisitAt(surface, userId) >= VISIT_GAP_MS;
}

function isRouteThrottled(surface, routePath) {
  if (typeof sessionStorage === 'undefined') return false;
  const raw = sessionStorage.getItem(routeKey(surface, routePath));
  if (!raw) return false;
  const ts = Number(raw);
  return Number.isFinite(ts) && Date.now() - ts < ROUTE_THROTTLE_MS;
}

function markRouteThrottled(surface, routePath) {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(routeKey(surface, routePath), String(Date.now()));
}

/** Clear visit markers when signing out of a portal. */
export function clearPlatformAccessSessionMarkers(surface, userId = null) {
  if (typeof localStorage === 'undefined' || !surface) return;
  if (userId) {
    localStorage.removeItem(visitKey(surface, userId));
    return;
  }
  const suffix = `:${surface}:`;
  const toRemove = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key?.startsWith(VISIT_PREFIX) && key.includes(suffix)) {
      toRemove.push(key);
    }
  }
  toRemove.forEach((key) => localStorage.removeItem(key));
}

async function insertViaRpc(surface, routePath) {
  const { data, error } = await supabase.rpc('log_platform_access', {
    p_surface: surface,
    p_device_category: detectDeviceCategory(),
    p_route_path: routePath,
  });

  if (error) {
    console.warn('log_platform_access RPC failed:', error.message, error.code);
    return false;
  }

  return Boolean(data);
}

async function insertViaTable(surface, routePath, userId) {
  const { error } = await supabase.from('platform_access_events').insert({
    surface,
    device_category: detectDeviceCategory(),
    route_path: routePath,
    user_id: userId,
  });

  if (error) {
    console.warn('platform_access_events insert failed:', error.message, error.code);
    return false;
  }

  return true;
}

/**
 * Record a portal access event.
 *
 * kind = 'visit'  → portal open / reopen / return after 30+ min away (used for "last login")
 * kind = 'route'  → in-app navigation, throttled per route (10 min)
 */
export async function recordPlatformAccessEvent({
  surface,
  routePath = null,
  userId = null,
  waitForSession = true,
  kind = 'route',
}) {
  if (!surface || !['academy', 'community'].includes(surface)) return false;

  if (waitForSession) {
    await waitForAuthSession(5000);
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const sessionUserId = sessionData?.session?.user?.id ?? null;
  const resolvedUserId = sessionUserId || userId;

  if (!resolvedUserId) return false;

  const isVisit = kind === 'visit';
  if (isVisit && !shouldLogVisit(surface, resolvedUserId)) return false;
  if (!isVisit && isRouteThrottled(surface, routePath)) return false;

  let ok = await insertViaRpc(surface, routePath);

  if (!ok) {
    ok = await insertViaTable(surface, routePath, resolvedUserId);
  }

  if (!ok) return false;

  if (isVisit) {
    markVisit(surface, resolvedUserId);
  } else {
    markRouteThrottled(surface, routePath);
  }

  return true;
}
